// This component is responsible for displaying and managing the list of users.

import useFetch from "../../../hooks/useFetch";
import { useContext, useEffect, useState } from "react";
import { PageHeader } from "../../../components/page-header/page-header";
import useToast from "../../../hooks/useToast";
import Utils from "../../../utils";
import {
  TDropdownOptions,
  TPrivilege,
  TResponseModel,
  TUser,
} from "../../../types/config";
import { TFilterModel } from "../../../types/config";
import DateUtils from "../../../utils/date";
import {
  eColumnConfigKeys,
  ePrivileges,
  eResultCode,
} from "../../../utils/enum";
import styles from "./users.module.css";
import UserForm from "./form";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import {
  CONFIG_GROUPS,
  dateOnlyFormat,
  defaultFilterParams,
  ROLEUNIQUEID,
  STAKE_HOLDERS,
} from "../../../utils/constants";
import GenericPopover from "../../../components/pop-over";
import {
  Button,
  Tag,
  Space,
  Input,
  Table,
  Drawer,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import CustomDrawer from "../../../components/drawer";
import {
  AddEditUser,
  GetConfigParamDataList,
  GetUserList,
} from "../../../utils/api.constant";
import { UserContext } from "../../../context";
import utils from "../../../utils";
import CustomModal from "../../../components/modal";
import ActionCard from "../../../components/actionCard/actionCard";
import {
  CloseOutlined,
  EditOutlined,
  FilterOutlined,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  CheckOutlined,
  TeamOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import ColumnManager from "../../../components/configurable-columns";
import ExportToCSV from "../../../components/exporttocsv/ExportToCsv";
import { ColumnConfigManager } from "../../../utils/column-management";
import BulkUploadUserForm from "./bulk-upload";

/*
 * UserList Component
 * This component is responsible for displaying and managing the list of users.
 * It handles fetching user data, searching, filtering, and rendering the user list.
 */
export default function UserList() {
  const { post } = useFetch();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TUser[]>([]);
  const { onShowToast } = useToast();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const context: any = useContext(UserContext);
  const [activeUser, setActiveUser] = useState<TUser>(context?.user);
  const [stakeHolderName, setStakeHolderName] = useState<string>(
    activeUser?.stakeHolderName || ""
  );
  const [selectedUser, setSelectedUser] = useState<TUser | null>(null); // State to keep track of selected user for modal
  const [stakeHolderId, setStakeHolderId] = useState<number | null>(null); // State to keep track of selected user for modal
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});

  useEffect(() => {
    setActiveUser(context?.user);
    setStakeHolderName(context?.user?.stakeHolderName);
  }, [context?.user]);

  useEffect(() => {
    const fetchDetails = async () => {
      const stakeHolderTypes = await fetchConfigParams(
        CONFIG_GROUPS.STAKEHOLDERS
      );

      const activeStakeHolder = stakeHolderTypes.find(
        (item: TDropdownOptions) =>
          item?.data?.paramUniqueId === stakeHolderName
      );

      if (activeStakeHolder) {
        setStakeHolderId(activeStakeHolder.value);
      }
    };
    fetchDetails();
  }, [stakeHolderName]);

  const fetchConfigParams = async (groupUniqueId: string) => {
    try {
      const payload = {
        data: {
          groupUniqueId,
          pageSize: -1,
        },
      };

      const response: TResponseModel = await post(
        GetConfigParamDataList,
        payload
      );
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          return Utils.mapDropdownOptions(data);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  const showDeleteModal = (user: TUser) => {
    setSelectedUser(user);
    setIsDeleteModalVisible(true);
  };

  const deleteUser = async (user: TUser) => {
    try {
      const payload = {
        data: {
          id: user.id,
          isDeleted: 1,
          stakeHolderId: stakeHolderId,
        },
      };
      const response = await post(AddEditUser, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        fetchData();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const showModal = (user: TUser) => {
    setSelectedUser(user);
    setIsModalVisible(true);
  };

  const handleOk = (userDetails: TUser) => {
    updateUserDetails(userDetails);
    setSelectedUser(null);
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setSelectedUser(null);
    setIsModalVisible(false);
  };

  const handleDeleteOk = async () => {
    if (selectedUser) {
      await deleteUser(selectedUser);
    }
    setSelectedUser(null);
    setIsDeleteModalVisible(false);
  };

  const handleDeleteCancel = () => {
    setSelectedUser(null);
    setIsDeleteModalVisible(false);
  };

  const initialColumns: any[] = [
    {
      title: "Name",
      dataIndex: "displayName", // Ensure this key exists in TUser
      key: "displayName",
      width: "30%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "displayName"),
      sorter: (a: TUser, b: TUser) =>
        a.displayName?.localeCompare(b.displayName ?? "") || 0,
      onFilter: (value: string | number | boolean, record: TUser) =>
        record.displayName?.includes(value as string),
    },
    {
      title: "Email ID",
      dataIndex: "emailId",
      key: "emailId",
      width: "30%",
      filterSearch: true,
      sorter: (a: TUser, b: TUser) =>
        a.emailId?.localeCompare(b.emailId ?? "") || 0,
      filters: utils.getUniqueFilters(data, "emailId"),
      onFilter: (value: string | number | boolean, record: TUser) =>
        record.emailId?.includes(value as string),
    },
    {
      title: "Mobile No.",
      dataIndex: "mobileNo",
      key: "mobileNo",
      width: "15%",
      filterSearch: true,
      sorter: (a: TUser, b: TUser) =>
        a.mobileNo?.localeCompare(b.mobileNo ?? "") || 0,
      filters: utils.getUniqueFilters(data, "mobileNo"),
      onFilter: (value: string | number | boolean, record: TUser) =>
        record.mobileNo?.includes(value as string),
    },
    {
      title: "Role",
      dataIndex: "userRoleName",
      key: "userRoleName",
      width: "15%",
      filterSearch: true,
      sorter: (a: TUser, b: TUser) =>
        a.userRoleName?.localeCompare(b.userRoleName ?? "") || 0,
      filters: utils.getUniqueFilters(data, "userRoleName"),
      onFilter: (value: string | number | boolean, record: TUser) =>
        record.userRoleName?.includes(value as string),
    },
    {
      title: "Created On",
      dataIndex: "createdOn",
      key: "createdOn",
      width: "15%",
      filterSearch: true,
      sorter: (a: TUser, b: TUser) =>
        String(a.createdOn).localeCompare(String(b.createdOn)),
      onFilter: (value: string | number | boolean, record: TUser) =>
        record.createdOn &&
        record.createdOn.toString().includes(value as string),
      render: (createdOn: string) => {
        return DateUtils.format(createdOn, dateOnlyFormat); // Apply date format here
      },
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      filters: [
        { text: "Active", value: "1" },
        { text: "In Active", value: "0" },
      ],
      width: "10%",
      filterSearch: true,
      sorter: (a: TUser, b: TUser) =>
        (a.isActive?.toString() || "").localeCompare(
          b.isActive?.toString() || ""
        ), // Handle undefined case
      onFilter: (value: string | number | boolean, record: TUser) =>
        record.isActive?.toString() === value,
      render: (isActive: boolean) => (
        <Tag color={isActive ? "var(--bs-primary)" : "#f50"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "actions",
      align: "center",
      width: "8%",
      render: (user: TUser) => (
        <>
          {context?.user?.roles[0]?.roleUniqueId !== ROLEUNIQUEID.SUPERADMIN &&
          user?.userRoleUniqueId === ROLEUNIQUEID.SUPERADMIN ? null : (
            <GenericPopover
              placement="left"
              content={
                <div className="standardActionBox">
                  {hasAddAccess() && (
                    <>
                      <CustomDrawer
                        size="large"
                        trigger={
                          <ActionCard text="Edit">
                            <EditOutlined />
                          </ActionCard>
                        }
                        component={UserForm}
                        componentProps={{
                          id: user.id,
                          activeStake: stakeHolderName,
                          onRefreshList: () => {
                            fetchData();
                          },
                        }}
                        title="User Form"
                      />
                      <ActionCard
                        text={user.isActive ? "Inactive" : "Active"}
                        onClick={() => showModal(user)}
                      >
                        {user.isActive ? (
                          <UserDeleteOutlined />
                        ) : (
                          <UserAddOutlined />
                        )}
                      </ActionCard>
                    </>
                  )}

                  {hasDeleteAccess() && (
                    <ActionCard
                      text="Delete"
                      onClick={() => showDeleteModal(user)}
                    >
                      <DeleteOutlined />
                    </ActionCard>
                  )}
                </div>
              }
              icon={
                <MoreOutlined style={{ fontSize: "20px", cursor: "pointer" }} />
              }
            />
          )}
        </>
      ),
    },
  ];

  useEffect(() => {
    fetchData();
    const updatedColumns = initialColumns.filter(
      (col) => col.key !== "orgName"
    );
    if (stakeHolderName !== STAKE_HOLDERS.IDEX) {
      const columnsToAdd: any = [
        {
          title: "Organization",
          dataIndex: "orgName",
          key: "orgName",
          width: "20%",
          filterSearch: true,
          sorter: (a: TUser, b: TUser) =>
            a.orgName?.localeCompare(b.orgName ?? "") || 0,
          filters: utils.getUniqueFilters(data, "orgName"),
          onFilter: (value: string | number | boolean, record: TUser) =>
            record.orgName?.includes(value as string),
        },
      ];
      if (stakeHolderName === STAKE_HOLDERS.SERVICEPARTNER) {
        columnsToAdd.push({
          title: "Exclusive Customer",
          dataIndex: "customerName",
          key: "customerName",
          width: "20%",
          filterSearch: true,
          sorter: (a: TUser, b: TUser) =>
            a.customerName?.localeCompare(b.customerName ?? "") || 0,
          filters: utils.getUniqueFilters(data, "customerName"),
          onFilter: (value: string | number | boolean, record: TUser) =>
            record.customerName?.includes(value as string),
          render: (customerName: string) =>
            customerName ? customerName : "N/A",
        });
      }
      updatedColumns.splice(4, 0, ...columnsToAdd);
    }
    setColumns(updatedColumns);
    setDraftColumns(updatedColumns);
  }, [stakeHolderName]);

  // const [columns, setColumns] = useState(initialColumns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [columns, setColumns] = useState(initialColumns); // Actual saved columns state
  const [draftColumns, setDraftColumns] = useState(columns); // Staging state for changes

  const updateUserDetails = async (values: TUser) => {
    try {
      const { isActive, ...restValues } = values;
      const payload = {
        data: {
          ...restValues,
          isActive: isActive ? 1 : 0,
          stakeHolderId: stakeHolderId,
        },
      };
      const response = await post(AddEditUser, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        fetchData();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      console.log("Finally");
    }
  };

  // To fetch the list of all the users
  const fetchData = async () => {
    if (stakeHolderName) {
      try {
        setIsLoading(true);
        const requestPayload = {
          data: {
            stakeHolderName,
            pageSize: -1,
            currentPage: 1,
          },
        };
        const response: TResponseModel = await post(
          GetUserList,
          requestPayload
        );
        const { data, dataResponse, filterModel } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          const formattedData = data.map((item: TUser, index: number) => ({
            key: (item.id ?? "").toString(),
            srNo: index + 1,
            displayName: item.displayName,
            emailId: item.emailId,
            mobileNo: item.mobileNo,
            userRoleName: item.userRoleName,
            orgName: item.orgName,
            createdOn: item.createdOn,
            status: item?.isActive ? "Active" : "In Active",
            isActive: item.isActive,
            id: item.id,
            customerName: item?.customerName,
            userRoleUniqueId: item?.userRoleUniqueId,
          }));
          setData(formattedData);
          setFilterParams({ ...(filterModel as TFilterModel) });
          setIsLoading(false);
          updateColumnFilters(filteredData);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const updateColumnFilters = (dataset: TUser[]) => {
    const nameFilters = Array.from(
      new Set(dataset.map((item) => item.displayName))
    ).map((displayName) => ({ text: displayName, value: displayName }));

    const emailFilters = Array.from(
      new Set(dataset.map((item) => item.emailId))
    ).map((emailId) => ({ text: emailId, value: emailId }));

    const mobileNoFilters = Array.from(
      new Set(dataset.map((item) => item.mobileNo))
    ).map((mobileNo) => ({ text: mobileNo, value: mobileNo }));

    const userRoleNameFilters = Array.from(
      new Set(dataset.map((item) => item.userRoleName))
    ).map((userRoleName) => ({
      text: userRoleName,
      value: userRoleName,
    }));

    const orgNameFilters = Array.from(
      new Set(
        dataset.filter((item) => item.orgName).map((item) => item.orgName)
      )
    ).map((orgName) => ({ text: orgName, value: orgName }));

    const customerFilters = Array.from(
      new Set(
        dataset
          .filter((item) => item.customerName)
          .map((item) => item.customerName)
      )
    ).map((customerName) => ({
      text: customerName,
      value: customerName,
    }));

    const statusFilters = [
      { text: "Active", value: "1" },
      { text: "In Active", value: "0" },
    ].filter((option) =>
      dataset.some((item) => item.isActive?.toString() === option.value)
    );

    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.key === "displayName") {
          return { ...col, filters: nameFilters };
        }
        if (col.key === "emailId") {
          return { ...col, filters: emailFilters };
        }
        if (col.key === "mobileNo") {
          return { ...col, filters: mobileNoFilters };
        }
        if (col.key === "userRoleName") {
          return { ...col, filters: userRoleNameFilters };
        }
        if (col.key === "orgName") {
          return { ...col, filters: orgNameFilters };
        }
        if (col.key === "customerName") {
          return { ...col, filters: customerFilters };
        }
        if (col.key === "isActive") {
          return { ...col, filters: statusFilters };
        }
        return col;
      })
    );
  };

  useEffect(() => {
    updateColumnFilters(filteredData);
  }, [activeFilters, data]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any
  ) => {
    setActiveFilters(filters);
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      })
    );
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const filteredData = data.filter((item) => {
    const searchMatch =
      searchText === "" ||
      Object.values(item).some((val) =>
        val?.toString().toLowerCase().includes(searchText.toLowerCase())
      );
    const displayNameMatch =
      !activeFilters.displayName ||
      activeFilters.displayName.includes(String(item.displayName));
    const emailIdMatch =
      !activeFilters.emailId ||
      activeFilters.emailId.includes(String(item.emailId));
    const mobileNoMatch =
      !activeFilters.mobileNo ||
      activeFilters.mobileNo.includes(String(item.mobileNo));
    const userRoleNameMatch =
      !activeFilters.userRoleName ||
      activeFilters.userRoleName.includes(String(item.userRoleName));
    const orgNameMatch =
      !activeFilters.orgName ||
      activeFilters.orgName.includes(String(item.orgName));
    const customerNameMatch =
      !activeFilters.customerName ||
      activeFilters.customerName.includes(String(item.customerName));
    const isActiveMatch =
      !activeFilters.isActive ||
      activeFilters.isActive.includes(String(item.isActive));
    return (
      searchMatch &&
      displayNameMatch &&
      emailIdMatch &&
      mobileNoMatch &&
      userRoleNameMatch &&
      orgNameMatch &&
      customerNameMatch &&
      isActiveMatch
    );
  });

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col
      )
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns.filter((col) => !col.hidden);

  const exportHeaders = columns
    .filter(
      (column) =>
        column.key && (column?.title as string)?.toLowerCase() !== "action"
    )
    .map((column) => ({
      label: column.title,
      key: column.key,
    }));

  const exportData = data.map((user) => ({
    ...user,
    isActive: user.isActive ? "Active" : "Inactive",
    createdOn: DateUtils.format(user.createdOn, dateOnlyFormat),
  }));

  const handleResetAll = () => {
    setActiveFilters({});
    setSearchText("");
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      }))
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };

  useEffect(() => {
    handleResetAll();
  }, [stakeHolderName]);

  const hasAddAccess = () => {
    if (stakeHolderName === STAKE_HOLDERS.CUSTOMER) {
      return Utils.isUserHasAccess(
        activeUser?.privilegeList as any,
        ePrivileges.ADDEDITCUSTUSERS
      );
    } else if (stakeHolderName === STAKE_HOLDERS.IDEX) {
      return Utils.isUserHasAccess(
        activeUser?.privilegeList as any,
        ePrivileges.ADDEDITIDEXUSERS
      );
    } else if (stakeHolderName === STAKE_HOLDERS.SERVICEPARTNER) {
      return Utils.isUserHasAccess(
        activeUser?.privilegeList as any,
        ePrivileges.ADDEDITPARTUSERS
      );
    }
    return false;
  };
  const hasDeleteAccess = () => {
    if (stakeHolderName === STAKE_HOLDERS.CUSTOMER) {
      return Utils.isUserHasAccess(
        activeUser?.privilegeList as any,
        ePrivileges.DELETECUSTUSERS
      );
    } else if (stakeHolderName === STAKE_HOLDERS.IDEX) {
      return Utils.isUserHasAccess(
        activeUser?.privilegeList as any,
        ePrivileges.DELETEIDEXUSERS
      );
    } else if (stakeHolderName === STAKE_HOLDERS.SERVICEPARTNER) {
      return Utils.isUserHasAccess(
        activeUser?.privilegeList as any,
        ePrivileges.DELETEPARTUSERS
      );
    }
    return false;
  };

  return (
    <div className="pageBody">
      <PageHeader
        icon={<TeamOutlined />}
        title={
          stakeHolderName === STAKE_HOLDERS.IDEX
            ? `${filterParams?.filterRowsCount} Total Idex Users`
            : stakeHolderName === STAKE_HOLDERS.CUSTOMER
              ? `${filterParams?.filterRowsCount} Total Customer Users`
              : stakeHolderName === STAKE_HOLDERS.SERVICEPARTNER
                ? `${filterParams?.filterRowsCount} Total Service Partner Users`
                : ""
        }
        size="large"
      >
        <Space>
          {hasAddAccess() && (
            <CustomDrawer
              trigger={
                <Button disabled={isLoading} type="primary">
                  Add
                </Button>
              }
              component={UserForm}
              componentProps={{
                id: 0,
                activeStake: stakeHolderName,
                onRefreshList: () => {
                  fetchData();
                },
              }}
              title="Dealer Form"
              size="large"
            />
          )}
          {stakeHolderName === STAKE_HOLDERS.SERVICEPARTNER &&
          Utils.isUserHasAccess(
            activeUser?.privilegeList as any,
            ePrivileges.BULKUPLOADENGINEERS
          ) ? (
            <CustomDrawer
              trigger={
                <Button disabled={isLoading} type="primary">
                  Bulk Upload
                </Button>
              }
              component={BulkUploadUserForm}
              componentProps={{
                onRefreshList: () => {
                  fetchData();
                },
              }}
              title="Bulk Upload User Form"
              size="default"
            />
          ) : null}

          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />
          <ExportToCSV
            data={exportData}
            headers={exportHeaders}
            fileName={`users_${DateUtils.format(new Date())}.csv`}
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined onClick={() => setDrawerVisible(true)} />
          </Tooltip>
        </Space>
      </PageHeader>
      <div className={styles.stakeholderContainer}>
        {Utils.isUserHasAccess(
          (activeUser?.privilegeList as TPrivilege[]) || [],
          ePrivileges.VIEWIDEXUSERS
        ) && (
          <div
            className={`${styles.tab} ${
              stakeHolderName === STAKE_HOLDERS.IDEX
                ? styles.activeTab
                : styles.inactiveTab
            }`}
            onClick={() => {
              setStakeHolderName(STAKE_HOLDERS.IDEX);
              setFilterParams(defaultFilterParams);
            }}
          >
            <span
              className={
                stakeHolderName === STAKE_HOLDERS.IDEX
                  ? styles.activeText
                  : styles.inactiveText
              }
            >
              {activeUser?.stakeHolderName === STAKE_HOLDERS.IDEX
                ? "Internal"
                : "Idex"}
            </span>
          </div>
        )}

        {Utils.isUserHasAccess(
          (activeUser?.privilegeList as TPrivilege[]) || [],
          ePrivileges.VIEWCUSTUSERS
        ) && (
          <div
            className={`${styles.tab} ${
              stakeHolderName === STAKE_HOLDERS.CUSTOMER
                ? styles.activeTab
                : styles.inactiveTab
            }`}
            onClick={() => {
              setStakeHolderName(STAKE_HOLDERS.CUSTOMER);
              setFilterParams(defaultFilterParams);
            }}
          >
            <span
              className={
                stakeHolderName === STAKE_HOLDERS.CUSTOMER
                  ? styles.activeText
                  : styles.inactiveText
              }
            >
              {activeUser?.stakeHolderName === STAKE_HOLDERS.CUSTOMER
                ? "Internal"
                : "Customer"}
            </span>
          </div>
        )}

        {Utils.isUserHasAccess(
          (activeUser?.privilegeList as TPrivilege[]) || [],
          ePrivileges.VIEWPARTUSERS
        ) && (
          <div
            className={`${styles.tab} ${
              stakeHolderName === STAKE_HOLDERS.SERVICEPARTNER
                ? styles.activeTab
                : styles.inactiveTab
            }`}
            onClick={() => {
              setStakeHolderName(STAKE_HOLDERS.SERVICEPARTNER);
              setFilterParams(defaultFilterParams);
            }}
          >
            <span
              className={
                stakeHolderName === STAKE_HOLDERS.SERVICEPARTNER
                  ? styles.activeText
                  : styles.inactiveText
              }
            >
              {activeUser?.stakeHolderName === STAKE_HOLDERS.SERVICEPARTNER
                ? "Internal"
                : "Service Partner"}
            </span>
          </div>
        )}
      </div>

      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            // ...pagination,
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total: any, range: any[]) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          sticky
          scroll={{
            y: "calc(100vh - 310px)", // Adjust height dynamically
          }}
        />
      </div>
      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>
      <CustomModal
        title="Confirm"
        isOpen={isModalVisible} // Ensure selectedUserId is valid
        okText="Yes"
        cusHeader={true}
        cancelText="No"
        onOk={() =>
          handleOk({
            ...selectedUser,
            isActive: !selectedUser?.isActive,
          } as any)
        }
        onCancel={() => handleCancel()}
      >
        <p>
          You are about to {selectedUser?.isActive ? "Deactivate" : "Activate"}{" "}
          <b>{selectedUser?.displayName}</b>
        </p>
      </CustomModal>
      <CustomModal
        title="Confirm Delete"
        isOpen={isDeleteModalVisible}
        onOk={handleDeleteOk}
        onCancel={() => handleDeleteCancel()}
        okText="Yes"
        cancelText="No"
        cusHeader={true}
      >
        <p>
          Are you about to delete <b>{selectedUser?.displayName}</b>?
        </p>
      </CustomModal>
    </div>
  );
}
